import { useState } from 'react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Table, TableHeader, TableHead, TableBody, TableCell, TableRow } from '@/components/ui/table';
import { ClinicalEventEntry } from './ClinicalEventEntry';
import { EpdsEntry } from './EpdsEntry';
import { postpartumCourseGroups } from './postpartumCourseFields';
import {
  postpartumCourseRows,
  maternalCourseColumns,
  newbornCourseColumns,
  type CourseEncounter,
} from './postpartumCourseProjection';
import type { BirthWorkspaceProps } from './LaborFlowsheet';
export interface PostpartumCourseProps extends BirthWorkspaceProps {
  encounters: CourseEncounter[];
  allowedSubjectIds?: string[];
  reviewedEpdsThreshold?: number | null;
  onOpenEncounter?: (id: string) => void;
}
export function PostpartumCourse(props: PostpartumCourseProps) {
  const [section, setSection] = useState('');
  const rows = postpartumCourseRows(props.events, props.episode, props.encounters, undefined, props.timeZone);
  const mother = props.episode.subjects.find(
    (s) =>
      s.subject_type === 'MOTHER' && (props.allowedSubjectIds === undefined || props.allowedSubjectIds.includes(s.id)),
  );
  const subjects = props.episode.subjects.filter(
    (s) =>
      s.active !== false &&
      s.subject_type !== 'FETUS' &&
      (props.allowedSubjectIds === undefined || props.allowedSubjectIds.includes(s.id)),
  );
  return (
    <Card>
      <CardHeader>
        <CardTitle>Postpartum course — mother and babies</CardTitle>
        <CardDescription>
          Visits stay aligned across the dyad. Confirmed observations are shown with links to their source entries;
          pending local entries remain in the offline queue.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-5">
        {props.episode.subjects
          .filter((s) => s.subject_type !== 'FETUS')
          .map((subject) => {
            const columns = subject.subject_type === 'MOTHER' ? maternalCourseColumns : newbornCourseColumns;
            return (
              <section key={subject.id}>
                <h3 className="mb-2 font-semibold">{subject.label}</h3>
                <Table className="min-w-[1400px]">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Visit / age</TableHead>
                      {columns.map((c) => (
                        <TableHead key={c}>{c}</TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {rows
                      .filter((r) => r.subjectId === subject.id)
                      .map((r) => (
                        <TableRow key={r.encounterId}>
                          <TableCell className="min-w-40">
                            <Button
                              type="button"
                              variant="ghost"
                              className="min-h-11 h-auto whitespace-normal"
                              onClick={() => props.onOpenEncounter?.(r.encounterId)}
                              disabled={!props.onOpenEncounter}>
                              {r.label} · {r.date.slice(0, 10)}
                            </Button>
                            <p>
                              {r.ageDays === null
                                ? 'Birth time unavailable'
                                : `${r.ageDays.toFixed(1)} days ${subject.subject_type === 'MOTHER' ? 'postpartum' : 'old'}`}
                            </p>
                          </TableCell>
                          {columns.map((c) => (
                            <TableCell key={c} className="min-w-36 whitespace-normal">
                              {r.cells[c]?.value ?? '—'}
                              {props.onEdit &&
                                r.cells[c]?.sourceIds.map((id) => {
                                  const event = props.events.find((e) => e.id === id);
                                  return event ? (
                                    <Button
                                      key={id}
                                      type="button"
                                      className="min-h-11"
                                      variant="ghost"
                                      onClick={() => props.onEdit?.(event)}>
                                      Review source
                                    </Button>
                                  ) : null;
                                })}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </section>
            );
          })}
        <div className="flex flex-wrap gap-2">
          {[...Object.keys(postpartumCourseGroups), 'EPDS'].map((label) => (
            <Button
              type="button"
              variant="outline"
              className="min-h-11"
              key={label}
              disabled={props.disabled || !props.encounterId}
              onClick={() => setSection(label)}>
              {label}
            </Button>
          ))}
        </div>
        <div data-encounter-draft>
          {Object.entries(postpartumCourseGroups).map(([label, descriptors]) => (
            <div hidden={section !== label} key={label}>
              <ClinicalEventEntry
                descriptors={descriptors}
                subjects={subjects}
                events={props.events}
                onSave={props.onSave}
                timeZone={props.timeZone}
                disabled={props.disabled || !props.encounterId}
              />
            </div>
          ))}
          {mother && (
            <div hidden={section !== 'EPDS'}>
              <EpdsEntry
                subjectId={mother.id}
                timeZone={props.timeZone}
                disabled={props.disabled || !props.encounterId}
                onSave={props.onSave}
                reviewedThreshold={props.reviewedEpdsThreshold}
              />
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
